import React, { useState } from 'react';
import { App, Form, Input } from 'antd';
import { changePassword, linkGoogleAccount } from '../../api/auth';
import { createAdminUser } from '../../api/adminUsers';
import { getApiErrorMessage } from '../../utils/apiError';
import AuthLayout from '../../components/AuthLayout';
import { InfoIcon, LockIcon, WarnIcon, MailIcon, GoogleG } from '../../components/AuthIcons';

export default function AdminProfile() {
  const { message, notification } = App.useApp();
  const [pwdForm] = Form.useForm();
  const [createAdminForm] = Form.useForm();

  const [pwdLoading, setPwdLoading] = useState(false);
  const [googleLinking, setGoogleLinking] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createdAdmin, setCreatedAdmin] = useState(null);

  const onChangePwd = async () => {
    try {
      setPwdLoading(true);
      const v = await pwdForm.validateFields();
      await changePassword({ current_password: v.current_password, new_password: v.new_password });
      message.success('Mot de passe changé avec succès.');
      pwdForm.resetFields();
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Changement de mot de passe échoué. Vérifiez le mot de passe actuel.'), placement: 'topRight' });
    } finally {
      setPwdLoading(false);
    }
  };

  const onCreateAdmin = async () => {
    try {
      setCreatingAdmin(true);
      setCreatedAdmin(null);
      const v = await createAdminForm.validateFields();
      const resp = await createAdminUser({ email: v.email, nom: v.nom, prenom: v.prenom });
      setCreatedAdmin(resp);
      message.success('Nouveau compte admin créé. Copiez le mot de passe temporaire maintenant.');
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Création du compte admin échouée.'), placement: 'topRight' });
    } finally {
      setCreatingAdmin(false);
    }
  };

  const linkGoogle = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      notification.error({ message: 'REACT_APP_GOOGLE_CLIENT_ID manquant côté client.', placement: 'topRight' });
      return;
    }
    if (typeof window === 'undefined' || !window.google?.accounts?.id) {
      notification.error({ message: 'Google Identity Services indisponible. Rechargez la page.', placement: 'topRight' });
      return;
    }

    setGoogleLinking(true);
    try {
      await new Promise((resolve, reject) => {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp) => {
            try {
              const id_token = resp?.credential;
              if (!id_token) throw new Error('id_token manquant');
              await linkGoogleAccount(id_token);
              resolve();
            } catch (e) {
              reject(e);
            }
          },
        });
        window.google.accounts.id.prompt((notif) => {
          const notDisplayed = typeof notif?.isNotDisplayed === 'function' && notif.isNotDisplayed();
          const skipped = typeof notif?.isSkippedMoment === 'function' && notif.isSkippedMoment();
          if (notDisplayed || skipped) {
            reject(new Error('Veuillez utiliser le bouton Google sur la page Login, puis réessayez.'));
          }
        });
      });
      message.success('Compte Google lié avec succès.');
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Liaison Google échouée.'), placement: 'topRight' });
    } finally {
      setGoogleLinking(false);
    }
  };

  const copyTempPassword = async () => {
    const pwd = createdAdmin?.temporary_password;
    if (!pwd) return;
    try {
      await navigator.clipboard.writeText(String(pwd));
      message.success('Copié');
    } catch {
      notification.error({ message: 'Copie impossible. Copiez manuellement.', placement: 'topRight' });
    }
  };

  return (
    <AuthLayout>
      <div>
        <div className="section-label">Sécurité</div>
        <div className="card">
          <div className="info-bar">
            {InfoIcon}
            <div>
              <div className="info-bar-title">Changer le mot de passe</div>
              <div className="info-bar-desc">Minimum 6 caractères, unique et difficile à deviner.</div>
            </div>
          </div>
          <div className="card-inner">
            <Form form={pwdForm} onFinish={onChangePwd} layout="vertical" requiredMark={false}>
              <div className="field">
                <label>Mot de passe actuel <span>*</span></label>
                <div className="input-wrap">
                  {LockIcon}
                  <Form.Item name="current_password" noStyle rules={[{ required: true }]}>
                    <Input.Password placeholder="••••••••" bordered={false} />
                  </Form.Item>
                </div>
              </div>

              <div className="field">
                <label>Nouveau mot de passe <span>*</span></label>
                <div className="input-wrap">
                  {LockIcon}
                  <Form.Item name="new_password" noStyle rules={[{ required: true, min: 8 }]}>
                    <Input.Password placeholder="••••••••" bordered={false} />
                  </Form.Item>
                </div>
              </div>

              <div className="field">
                <label>Confirmer le mot de passe <span>*</span></label>
                <div className="input-wrap">
                  {LockIcon}
                  <Form.Item
                    name="confirm_password"
                    noStyle
                    dependencies={['new_password']}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                          return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="••••••••" bordered={false} />
                  </Form.Item>
                </div>
              </div>

              <button className="btn-primary" type="submit" disabled={pwdLoading}>
                {pwdLoading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </Form>
          </div>
        </div>
      </div>

      <div>
        <div className="section-label">Google</div>
        <div className="card">
          <div className="info-bar">
            {InfoIcon}
            <div>
              <div className="info-bar-title">Lier votre compte Google</div>
              <div className="info-bar-desc">Connectez-vous via Google en conservant le rôle admin.</div>
            </div>
          </div>
          <div className="card-inner">
            <button className="btn-outline" type="button" onClick={linkGoogle} disabled={googleLinking}>
              {GoogleG}
              {googleLinking ? 'Liaison en cours...' : 'Lier mon compte Google'}
            </button>
            <div className="help-text">
              {process.env.REACT_APP_GOOGLE_CLIENT_ID ? null : (
                <span style={{ fontSize: 11, color: '#aaa' }}>REACT_APP_GOOGLE_CLIENT_ID manquant côté client.</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="section-label">Créer un nouvel admin</div>
        <div className="card">
          <div className="info-bar warn-bar">
            {WarnIcon}
            <div>
              <div className="info-bar-title">Admin local — mot de passe temporaire</div>
              <div className="info-bar-desc">Email Google exact. Le mot de passe s'affiche une seule fois.</div>
            </div>
          </div>
          <div className="card-inner">
            <Form form={createAdminForm} onFinish={onCreateAdmin} layout="vertical" requiredMark={false}>
              <div className="field">
                <label>Email <span>*</span></label>
                <div className="input-wrap">
                  {MailIcon}
                  <Form.Item name="email" noStyle rules={[{ required: true, type: 'email' }]}>
                    <Input type="email" placeholder="koffimea71@gmail.com" bordered={false} />
                  </Form.Item>
                </div>
              </div>

              <div className="two-col">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Nom</label>
                  <div className="input-wrap">
                    <Form.Item name="nom" noStyle>
                      <Input type="text" placeholder="Nom" bordered={false} />
                    </Form.Item>
                  </div>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Prénom</label>
                  <div className="input-wrap">
                    <Form.Item name="prenom" noStyle>
                      <Input type="text" placeholder="Prénom" bordered={false} />
                    </Form.Item>
                  </div>
                </div>
              </div>

              <button className="btn-primary" type="submit" disabled={creatingAdmin}>
                {creatingAdmin ? "Création..." : "Créer l'admin"}
              </button>
            </Form>

            {createdAdmin?.temporary_password ? (
              <div className="temp-row">
                <div className="temp-title">Mot de passe temporaire</div>
                <div className="temp-actions">
                  <div className="input-wrap" style={{ height: 34 }}>
                    <Input value={createdAdmin.temporary_password} readOnly bordered={false} />
                  </div>
                  <button type="button" className="copy-btn" onClick={copyTempPassword}>
                    Copier
                  </button>
                </div>
                <div className="help-text">
                  Connecte-toi ensuite avec cet email + ce mot de passe, change le mot de passe, puis utilise “Lier mon compte Google”.
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

